import React from 'react';
import { graphql } from 'gatsby';
//@ts-ignore
import * as styles from './highlighted-content-block.module.css';
import RichText from '../Global/RichText';
import CtaLink from '../Global/CtaLink';
//@ts-ignore
import { BuildLinkHref } from '../Global/BuildLinkHref';
import {
    CTAConfiguration,
    GeneralImages,
    KontentRadioMultiChoice,
    BrandColor,
    KontentRichText,
    KontentStringValue,
} from '../../../types/KontentProps';
import {
    getColor,
    getMediaData,
    getMultiChoice,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
    getDisplayDimensions,
} from '../Global/DataUtils';
import PlainTextElement from '../Global/Headlines';
import AnchorLink from '../Global/AnchorLink';
import LazyImage from '../Global/LazyImage';

type ImagePositionType = 'right' | 'left' | undefined;
type LinkImg = 'yes' | 'no' | undefined;
interface Props extends CTAConfiguration, GeneralImages {
    kontentItemId?: string;
    headline: KontentRichText;
    paragraph?: KontentRichText;
    subheadline?: KontentRichText;
    image_position?: KontentRadioMultiChoice<ImagePositionType>;
    link_image?: KontentRadioMultiChoice<LinkImg>;
    content_panel__content_panel_color?: BrandColor;
    content_panel__content_panel_font_color?: BrandColor;
    hide_cta: {
        value: [
            {
                codename: string;
            }
        ];
    };
    cta_configuration__cta_location_anchor_point: KontentStringValue;
}

const HighlightedContentBlock: React.FC<Props> = ({
    content_panel__content_panel_color,
    content_panel__content_panel_font_color,
    cta_configuration__cta_label,
    cta_configuration__cta_location,
    cta_configuration__cta_location_anchor_point,
    cta_configuration__cta_window_action,
    general_large___small_image_selection__large,
    general_large___small_image_selection__small,
    headline,
    image_position,
    link_image,
    paragraph,
    subheadline,
    hide_cta,
    kontentItemId,
}) => {
    const hideCta = hide_cta.value[0]?.codename;
    const largeBgImage = getMediaData(
        general_large___small_image_selection__large
    );
    const smallBgImage = getMediaData(
        general_large___small_image_selection__small
    );
    const imagePosition = getMultiChoice(image_position);
    const imgLinked = getMultiChoice(link_image) === 'yes';
    const imagePositionCase = imagePosition
        ? imagePosition.toLowerCase()
        : null;
    const ctaWindowAction = getMultiChoice(
        cta_configuration__cta_window_action
    );
    const contentPanelColor = getColor(content_panel__content_panel_color);
    const contentPanelFontColor = getColor(
        content_panel__content_panel_font_color
    );
    const largeBgImageUrl = largeBgImage?.url;
    const smallBgImageUrl = smallBgImage?.url;
    let imageContentPosition = setInnerContentPosition(imagePositionCase);
    const ctaHref = cta_configuration__cta_location_anchor_point?.value
        ? BuildLinkHref(cta_configuration__cta_location) +
          '/#' +
          cta_configuration__cta_location_anchor_point?.value
        : BuildLinkHref(cta_configuration__cta_location);

    const ctaTarget = ctaWindowAction === 'new_window' ? '_blank' : undefined;

    const imgContent = largeBgImageUrl ? (
        <picture
            className={styles.picture}
            data-kontent-element-codename="image"
        >
            {smallBgImageUrl && (
                <source
                    media="(max-width:576px)"
                    srcSet={buildKontentSrcSet(
                        smallBgImageUrl,
                        imagePreset.halfWidth.mobile.w,
                        {
                            fm: 'webp' as const,
                            q: imagePreset.halfWidth.mobile.q,
                        }
                    )}
                    sizes="100vw"
                    type="image/webp"
                />
            )}
            <source
                media="(max-width:991px)"
                srcSet={buildKontentSrcSet(
                    largeBgImageUrl,
                    imagePreset.halfWidth.mobile.w,
                    {
                        fm: 'webp' as const,
                        q: imagePreset.halfWidth.mobile.q,
                    }
                )}
                sizes="100vw"
                type="image/webp"
            />
            <LazyImage
                src={optimizeKontentImageUrl(
                    largeBgImageUrl,
                    imagePreset.halfWidth.desktop
                )}
                srcSet={buildKontentSrcSet(
                    largeBgImageUrl,
                    imagePreset.halfWidth.desktop.w,
                    { fm: 'webp' as const, q: 85 }
                )}
                sizes="(max-width: 991px) 100vw, 50vw"
                alt={largeBgImage?.description || ''}
                {...getDisplayDimensions(
                    largeBgImage?.width,
                    largeBgImage?.height,
                    imagePreset.halfWidth.desktop.w
                )}
            />
        </picture>
    ) : null;

    return (
        <div data-kontent-item-id={kontentItemId}>
            <div className={styles.imageTextArea + ' ' + imageContentPosition}>
                <div className={styles.pictureBody}>
                    {imgLinked &&
                    cta_configuration__cta_location?.value?.[0]?.elements?.slug
                        ?.url ? (
                        <a
                            href={ctaHref}
                            target={ctaTarget}
                            rel={
                                ctaTarget === '_blank'
                                    ? 'noopener noreferrer'
                                    : undefined
                            }
                            aria-label={
                                cta_configuration__cta_label?.value ||
                                'View content'
                            }
                        >
                            {imgContent}
                        </a>
                    ) : imgLinked &&
                      !cta_configuration__cta_location?.value?.[0]?.elements
                          ?.slug &&
                      cta_configuration__cta_location_anchor_point?.value &&
                      typeof window !== 'undefined' ? (
                        <a
                            href={`${window.location.pathname}#${cta_configuration__cta_location_anchor_point?.value}`}
                            aria-label={
                                cta_configuration__cta_label?.value ||
                                'View content'
                            }
                        >
                            {imgContent}
                        </a>
                    ) : (
                        imgContent
                    )}
                </div>
                {contentPanelColor && contentPanelFontColor && (
                    <div
                        className={styles.contentBox}
                        style={{
                            backgroundColor: contentPanelColor,
                            color: contentPanelFontColor,
                        }}
                    >
                        <div className={styles.infoItems}>
                            {headline?.value && (
                                <div className={styles.headline}>
                                    <h2>
                                        <PlainTextElement
                                            text={headline}
                                            elementCodename="headline"
                                        />
                                    </h2>
                                </div>
                            )}

                            {subheadline?.value && (
                                <div className={styles.subheadline}>
                                    {RichText(
                                        subheadline,
                                        false,
                                        undefined,
                                        undefined,
                                        'subheadline'
                                    )}
                                </div>
                            )}

                            {paragraph?.value && (
                                <div className={styles.paragraph}>
                                    {RichText(
                                        paragraph,
                                        false,
                                        undefined,
                                        undefined,
                                        'paragraph'
                                    )}
                                </div>
                            )}
                            {hideCta !== 'yes' && (
                                <div className={styles.ctaContainer}>
                                    {!cta_configuration__cta_location?.value
                                        ?.length &&
                                    cta_configuration__cta_location_anchor_point.value ? (
                                        <AnchorLink
                                            targetId={
                                                cta_configuration__cta_location_anchor_point?.value
                                            }
                                            color={contentPanelFontColor}
                                            visualStyle="outlineLight"
                                        >
                                            {cta_configuration__cta_label?.value ||
                                                ''}
                                        </AnchorLink>
                                    ) : (
                                        <CtaLink
                                            href={ctaHref}
                                            target={ctaTarget}
                                            visualStyle="outlineLight"
                                            color={contentPanelFontColor}
                                        >
                                            {cta_configuration__cta_label?.value ||
                                                ''}
                                        </CtaLink>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default HighlightedContentBlock;

export const fragmentHighlightContentBlock = graphql`
    fragment HighlightContentBlock on kontent_item_component___highlight_content_block {
        elements {
            content_panel__content_panel_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                            color_name {
                                value
                            }
                        }
                    }
                }
            }
            content_panel__content_panel_font_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                            color_name {
                                value
                            }
                        }
                    }
                }
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
            general_large___small_image_selection__large {
                value {
                    ... on kontent_item_media___image {
                        id
                        elements {
                            caption {
                                value
                            }
                            file {
                                value {
                                    url
                                    description
                                    name
                                    width
                                    height
                                }
                            }
                        }
                    }
                }
            }
            general_large___small_image_selection__small {
                value {
                    ... on kontent_item_media___image {
                        id
                        elements {
                            caption {
                                value
                            }
                            file {
                                value {
                                    url
                                    description
                                    name
                                    width
                                    height
                                }
                            }
                        }
                    }
                }
            }
            headline {
                value
            }
            image_position {
                value {
                    codename
                }
            }
            link_image {
                value {
                    codename
                }
            }
            paragraph {
                ...kontentRichText
            }
            subheadline {
                links {
                    codename
                }
                value
            }
            hide_cta {
                value {
                    codename
                }
            }
        }
    }
`;

function setInnerContentPosition(imagePositionCase: ImagePositionType) {
    if (imagePositionCase == 'left') {
        return styles.imageLeft;
    }

    if (imagePositionCase == 'right') {
        return styles.imageRight;
    }

    return {};
}
